import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { chatApiInstance } from "../apis/chatApi";
import { formatTimestamp } from "../utils/toolAuditLogUtils";

interface UndoAuditRow {
    id: number;
    call_id: string;
    tool_name: string;
    param_json: string | Record<string, unknown> | null;
    output_json: string | Record<string, unknown> | null;
    status: string;
    start_timestamp: string | null;
    end_timestamp: string | null;
    user_email?: string | null;
    action_permission?: string | null;
    /** Server-derived: true iff the action name is in the undoable allowlist. */
    is_undoable?: boolean | null;
    /** Human-readable description from the server (reasoning, else humanized tool_name). */
    description?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    aiThreadId: string;
    userEmail: string;
    preSelectedCallIds?: string[];
}

function humanizeToolName(name: string): string {
    return name
        .replace(/_/g, " ")
        .replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

const UndoActionsModal: React.FC<Props> = ({ open, onClose, aiThreadId, userEmail, preSelectedCallIds }) => {
    const [rows, setRows] = useState<UndoAuditRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedCallIds, setSelectedCallIds] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !aiThreadId) return;
        setRows([]);
        setSelectedCallIds(new Set());
        setSubmitError(null);
        setLoading(true);
        chatApiInstance
            .getToolAuditLogs(aiThreadId)
            .then((data: unknown) => {
                const rawRows = Array.isArray(data) ? data : [];
                // Only show rows the server flagged as undoable (allowlisted + not themselves reversals).
                const filtered = (rawRows as UndoAuditRow[]).filter((r) => r.is_undoable === true);
                setRows(filtered);

                // Only pre-check rows that actually pass the full selectable predicate.
                const isSelectableRow = (r: UndoAuditRow): boolean =>
                    r.is_undoable === true &&
                    String(r.status).toUpperCase() === "SUCCESS";

                const selectableIds = new Set(filtered.filter(isSelectableRow).map((r) => r.call_id));
                const seedIds = (preSelectedCallIds ?? []).filter((id) => selectableIds.has(id));
                setSelectedCallIds(new Set(seedIds));
            })
            .catch(() => {
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [open, aiThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleCallId = (callId: string) => {
        setSelectedCallIds((prev) => {
            const next = new Set(prev);
            if (next.has(callId)) {
                next.delete(callId);
            } else {
                next.add(callId);
            }
            return next;
        });
    };

    const handleUndo = async () => {
        if (selectedCallIds.size === 0 || submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await chatApiInstance.handleUndoAction(aiThreadId, userEmail, Array.from(selectedCallIds));
            onClose();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Undo request failed.";
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const isRowSelectable = (row: UndoAuditRow): boolean =>
        row.is_undoable === true &&
        String(row.status).toUpperCase() === "SUCCESS";

    const getDisabledTooltip = (row: UndoAuditRow): string => {
        if (String(row.status).toUpperCase() !== "SUCCESS") return "Only successfully completed actions can be undone";
        return "Undo not supported for this action";
    };

    const handleClose = () => {
        setSelectedCallIds(new Set());
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle
                sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontWeight: 600,
                    fontSize: 18,
                    color: "#19213D",
                    borderBottom: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <UndoIcon sx={{ color: "#666F8D", fontSize: 20 }} />
                    <span>Undo Actions</span>
                </Box>
                <IconButton size="small" onClick={handleClose} sx={{ color: "#666F8D", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, minHeight: 160 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                        <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#666F8D", fontSize: 14 }}>
                            No actions in this session yet.
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {rows.map((row) => {
                            const selectable = isRowSelectable(row);
                            const callId = row.call_id;
                            const checked = selectedCallIds.has(callId);
                            const ts = formatTimestamp(row.start_timestamp);
                            const statusStr = String(row.status ?? "").toUpperCase();
                            // Use server-provided description;
                            // fall back to humanized tool name if description is absent.
                            const title = row.description || humanizeToolName(row.tool_name);
                            return (
                                <ListItem
                                    key={row.id}
                                    disablePadding
                                    divider
                                    sx={{ px: 2, py: 1, minHeight: 56, alignItems: "flex-start", opacity: selectable ? 1 : 0.5 }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Tooltip
                                                title={selectable ? "" : getDisabledTooltip(row)}
                                                placement="top"
                                                disableHoverListener={selectable}
                                                disableFocusListener={selectable}
                                                disableTouchListener={selectable}
                                            >
                                                <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={checked}
                                                        disabled={!selectable}
                                                        onChange={() => toggleCallId(callId)}
                                                        sx={{ p: 0 }}
                                                    />
                                                </span>
                                            </Tooltip>
                                            <Typography
                                                sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 13, color: "#19213D", wordBreak: "break-word" }}
                                            >
                                                {title}
                                            </Typography>
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    fontSize: 11,
                                                    fontFamily: "Inter",
                                                    fontWeight: 600,
                                                    backgroundColor: statusStr === "SUCCESS" ? "#dcfce7" : "#fee2e2",
                                                    color: statusStr === "SUCCESS" ? "#166534" : "#991b1b",
                                                }}
                                            >
                                                {statusStr}
                                            </Box>
                                            <Typography sx={{ fontFamily: "Inter", fontSize: 11, color: "#9CA3AF", ml: "auto" }}>
                                                {ts}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>

            <DialogActions
                sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB", flexDirection: "column", alignItems: "flex-end", gap: 1 }}
            >
                {submitError && (
                    <Typography sx={{ fontFamily: "Inter", fontSize: 12, color: "#dc2626", width: "100%", textAlign: "left" }}>
                        {submitError}
                    </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={handleClose} disabled={submitting} sx={{ fontFamily: "var(--theo-font-sans)" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUndo}
                        disabled={selectedCallIds.size === 0 || submitting}
                        startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <UndoIcon />}
                        sx={{ fontFamily: "var(--theo-font-sans)", backgroundColor: "#19213D", "&:hover": { backgroundColor: "#2d3748" } }}
                    >
                        Undo selected
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UndoActionsModal;
